import {
  GET_SURVEY_RESPONSES,
  GET_PUBLIC_SURVEY_RESPONSES,
  RESET_SURVEY_RESPONSES,
  GET_QUALTRICS_SURVEY_RESPONSES,
} from '../../fetchTypes';
import {
  createDefaultRequestInitialState,
  createSelectorById,
  createSelectorByIdList,
  defaultRequestByIdReducer,
} from './withFetch';

const baseInitialState = {
  submissions: { complete: [], incomplete: [] },
  headers: [],
};

const initialState = createDefaultRequestInitialState();

export const actionDataToId = (data) => data.projectId;

// Qualtrics response transformation function
const transformQualtricsResponses = (qualtricsResponse, qidToDataExportTagMapping = {}) => {
  if (!qualtricsResponse?.data?.response) {
    return { submissions: { complete: [], incomplete: [] }, headers: [] };
  }

  const responses = qualtricsResponse.data.response;
  const headers = [];
  const completeResponses = [];
  const incompleteResponses = [];

  // Extract headers from first response + question metadata
  if (responses.length > 0) {
    const firstResponse = responses[0];
    
    // Add basic LimeSurvey headers
    headers.push('id', 'submitdate', 'lastpage', 'startlanguage', 'startdate', 'datestamp', 'ipaddr');
    
    // For MC+MAVR questions, we need to get all possible choices from ALL responses
    // not just the selected ones in first response
    const processedQIDs = new Set();
    
    // First pass: collect all unique QIDs and their potential choice ranges
    const qidChoiceMap = {};
    responses.forEach(response => {
      Object.keys(response.values).forEach(key => {
        if (key.startsWith('QID')) {
          const baseQID = key.split('_')[0].split('#')[0];
          const value = response.values[key];
          
          if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'string') {
            // This looks like MC+MAVR choices - collect all possible choices
            if (!qidChoiceMap[baseQID]) {
              qidChoiceMap[baseQID] = new Set();
            }
            value.forEach(choice => qidChoiceMap[baseQID].add(choice));
          }
        }
      });
    });
    
    // Add question-specific headers based on response values and choice mapping
    Object.keys(firstResponse.values).forEach(key => {
      if (key.startsWith('QID')) {
        const baseQID = key.split('_')[0].split('#')[0];
        const dataExportTag = qidToDataExportTagMapping[baseQID] || baseQID.replace('QID', 'Q');
        const value = firstResponse.values[key];
        
        if (!processedQIDs.has(baseQID)) {
          processedQIDs.add(baseQID);
          
          // Special handling for HeatMap coordinates
          if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'object' && value[0].x !== undefined && value[0].y !== undefined) {
            // Add headers for each coordinate point
            value.forEach((coordinate, index) => {
              const pointIndex = index + 1;
              headers.push(`${dataExportTag}[Point ${pointIndex} ~ Position]`);
              headers.push(`${dataExportTag}[Point ${pointIndex} ~ State]`);
              headers.push(`${dataExportTag}[Point ${pointIndex} ~ Comment]`);
            });
          } else if (Array.isArray(value) && qidChoiceMap[baseQID]) {
            // Multiple choice headers - add ALL possible choices, not just selected ones
            const allChoices = Array.from(qidChoiceMap[baseQID]).sort((a, b) => parseInt(a) - parseInt(b));
            allChoices.forEach(choice => {
              const headerName = `${dataExportTag}[${choice}]`;
              headers.push(headerName);
            });
          } else if (key.includes('_TEXT')) {
            headers.push(dataExportTag);
          } else if (key.includes('_') || key.includes('#')) {
            headers.push(key.replace(baseQID, dataExportTag));
          } else {
            headers.push(dataExportTag);
          }
        }
      }
    });
  }

  // Transform responses
  responses.forEach((response, index) => {
    // Start with basic LimeSurvey fields
    const transformedResponse = {
      id: response.responseId,
      submitdate: response.values.finished === 1 ? response.values.endDate : null,
      lastpage: response.values.progress || 0,
      startlanguage: response.values.userLanguage || 'en',
      startdate: response.values.startDate,
      datestamp: response.values.recordedDate,
      ipaddr: response.values.ipAddress,
    };
    
    // Transform Qualtrics question fields to LimeSurvey format
    Object.keys(response.values).forEach(key => {
      if (key.startsWith('QID')) {
        const baseQID = key.split('_')[0].split('#')[0];
        const dataExportTag = qidToDataExportTagMapping[baseQID] || baseQID.replace('QID', 'Q');
        const value = response.values[key];
        
        // Special handling for HeatMap coordinates
        if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'object' && value[0].x !== undefined && value[0].y !== undefined) {
          // This is a HeatMap coordinate array - transform each coordinate
          value.forEach((coordinate, index) => {
            const pointIndex = index + 1;
            
            // Try to get image dimensions from question metadata (this would come from question properties)
            // For now, use common dimensions or fallback values
            // TODO: Get actual image dimensions from question properties
            const imageDimensions = { width: 320, height: 200 }; // Common HeatMap image size, adjust as needed
            
            // Normalize coordinates to 0-1 range
            const normalizedX = (coordinate.x / imageDimensions.width);
            const normalizedY = (coordinate.y / imageDimensions.height);
            
            // Ensure coordinates are within 0-1 range
            const clampedX = Math.max(0, Math.min(1, normalizedX));
            const clampedY = Math.max(0, Math.min(1, normalizedY));
            
            transformedResponse[`${dataExportTag}[Point ${pointIndex} ~ Position]`] = `${clampedX.toFixed(4)},${clampedY.toFixed(4)}`;
            transformedResponse[`${dataExportTag}[Point ${pointIndex} ~ State]`] = '+'; // Default to positive state for HeatMap clicks
            transformedResponse[`${dataExportTag}[Point ${pointIndex} ~ Comment]`] = ''; // Empty comment by default
          });
        } else if (key.includes('_TEXT')) {
          // Text question
          transformedResponse[dataExportTag] = value;
        } else if (key.includes('_') && !key.includes('#')) {
          // Matrix question (QID7_1, QID7_2, etc.)
          // Special handling for Matrix+Likert+MultipleAnswer - preserve array format
          if (Array.isArray(value) && value.length > 0) {
            // This is a Matrix+Likert+MultipleAnswer subquestion with array response
            // Convert field name to bracket format but preserve array value
            
            // Extract subquestion number from QID4_1 format
            const parts = key.split('_');
            const subquestionNumber = parts[1];
            
            // Convert to bracket format: QID4_1 → Q4[1] but keep array value
            const fieldName = `${dataExportTag}[${subquestionNumber}]`;
            transformedResponse[fieldName] = value; // Keep as array: ["1"] or ["3", "4"]
            
          } else {
            // Regular Matrix question with single value
            const suffix = key.substring(key.indexOf('_'));
            transformedResponse[`${dataExportTag}${suffix}`] = String(value);
          }
        } else if (Array.isArray(value)) {
          // Multiple choice question - convert array to individual Y/N fields
          
          
          value.forEach(choice => {
            const fieldName = `${dataExportTag}[${choice}]`;
            transformedResponse[fieldName] = 'Y';
          });
          
        } else if (key.includes('#')) {
          // Side by side question (QID11#1_1, QID11#2_2, etc.)
          const suffix = key.substring(key.indexOf('#'));
          transformedResponse[`${dataExportTag}${suffix}`] = String(value);
        } else {
          // Simple single-choice question
          transformedResponse[dataExportTag] = String(value);
        }
      }
    });

    // Categorize by completion status
    if (response.values.finished === 1) {
      completeResponses.push(transformedResponse);
    } else {
      incompleteResponses.push(transformedResponse);
    }
  });

  return {
    submissions: {
      complete: completeResponses,
      incomplete: incompleteResponses
    },
    headers
  };
};

const reducer = defaultRequestByIdReducer({
  actionDataToId,
  resultMapper: (data, action) => {
    // Extract QID mapping from action data outside try block
    const qidToDataExportTagMapping = action?.data?.qidToDataExportTagMapping || {};
    
    try {
      // Handle Qualtrics responses differently
      if (action && action.fetchType === GET_QUALTRICS_SURVEY_RESPONSES) {
        return transformQualtricsResponses(data, qidToDataExportTagMapping);
      }

      // Check if this might be Qualtrics data even without proper action
      if (data && data.data && data.data.response && Array.isArray(data.data.response)) {
        return transformQualtricsResponses(data, qidToDataExportTagMapping);
      }
      
      // Also check for direct response array format
      if (data && data.response && Array.isArray(data.response)) {
        // Transform the data to match what transformQualtricsResponses expects
        const qualtricsFormattedData = {
          data: {
            response: data.response
          }
        };
        return transformQualtricsResponses(qualtricsFormattedData, qidToDataExportTagMapping);
      }

      // Default LimeSurvey format
      let result = null;
      let headers = [];
      const { response } = data || {};
      if (!response || !response.length) {
        // response is empty array when the survey isn't live for some reason :p
        return { submissions: { complete: [], incomplete: [] }, headers: [] };
      }
      
      result = atob(response); //.replace(/\\"/g, '"');
      headers =
        ((result || '').split(/\s*(?:{|})\s*/g)[3] || '').match(
          /"[^"]+"\s*:/g
        ) || [];
      headers = headers.map((header) => header.split(/"/g).filter((x) => x)[0]);
      // calculate header keys from the original json text to make sure they are correctly ordered
      result = (JSON.parse(result) || {}).responses || [];
      result = result.map((submission) => Object.values(submission)[0]);

      //all assumptions depend on that you can only request only incomplete or only complete responses.
      if (result[0].submitdate) {
        return { submissions: { complete: result, incomplete: [] }, headers };
      } else {
        return { submissions: { incomplete: result, complete: [] }, headers };
      }
    } catch (e) {
      // If it's an atob error and we have Qualtrics-like data, try to handle it
      if (e.name === 'InvalidCharacterError' && data && data.response && Array.isArray(data.response)) {
        // Transform the data to match what transformQualtricsResponses expects
        const qualtricsFormattedData = {
          data: {
            response: data.response
          }
        };
        
        return transformQualtricsResponses(qualtricsFormattedData, qidToDataExportTagMapping);
      }
      
      return { submissions: { complete: [], incomplete: [] }, headers: [] };
    }
  },
  baseInitialState,
  extraOnSuccess: (state, projectId) => {
    const nextState = { ...state };
    // const nextState = { ...state, questionByQid: { ...state.questionByQid } };

    return nextState;
  },
});

export default (state = initialState, action) => {
  switch (action.fetchType) {
    case GET_SURVEY_RESPONSES:
      return reducer(state, action);
    case GET_PUBLIC_SURVEY_RESPONSES:
      return reducer(state, action);
    case GET_QUALTRICS_SURVEY_RESPONSES:
      return reducer(state, action);
    case RESET_SURVEY_RESPONSES:
      return reducer(initialState, action);
    default:
      return state;
  }
};

const selfSelector = (state) => state.fetcher.surveyResponses;
export const selectFetcherSurveyResponses = selfSelector;
export const selectSurveyResponsesByProjectId =
  createSelectorById(selfSelector);
export const selectSurveyResponsesListByProjectIdList =
  createSelectorByIdList(selfSelector);
